import React, { useCallback, useRef, useState } from 'react';
import TableHeader from '../../../BaseComponents/TableHeader';
import './BodySystemsTable.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import BodySystemsTableRow from './BodySystemsTableRow';
import ConfirmModal from '../../../BaseComponents/Modal/ConfirmModal';
import {
  changeDeleteBodySystemModalVisibility,
  setBodySystemsList,
  setBodySystemToEdit,
} from '../../../../store/appSlice';
import { deleteBodySystem } from '../../../../api/body-systems';
import {
  BodySystemPromptField,
  ListBodySystemResponseElement,
} from '../../../../types/apiGeneratedTypes';
import { toast } from 'react-toastify';

const headerColumns = [
  { title: 'Icon', key: 'icon', minWidth: '80px', flex: 0 },
  { title: 'Name', key: 'name', minWidth: '200px', flex: 0 },
  { title: 'Text', key: 'text', minWidth: '188px', flex: 1 },
  { title: 'Biomarkers', key: 'biomarkers', minWidth: '220px', flex: 0 },
  { title: 'MRI Conditions', key: 'mri_conditions', minWidth: '260px', flex: 0 },
  { title: 'Applicable Modalities', key: 'applicable_modalities', minWidth: '260px', flex: 0 },
  { title: 'Prompts', key: 'prompts', minWidth: '160px', flex: 0 },
  { title: '', key: 'actions', minWidth: '48px', flex: 0 },
];

type Props = {
  onEditPrompt: (
    bodySystem: ListBodySystemResponseElement,
    field: BodySystemPromptField
  ) => void;
  onGenerateScores: (bodySystem: ListBodySystemResponseElement) => void;
};

const BodySystemsTable: React.FC<Props> = ({ onEditPrompt, onGenerateScores }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { bodySystemsList, isDeleteBodySystemModalOpened, bodySystemToEdit } =
    useSelector((state: RootState) => state.app);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleCloseDeleteModalClick = useCallback(() => {
    dispatch(changeDeleteBodySystemModalVisibility(false));
    setErrorMessage(null);
  }, [dispatch]);

  const handleDelete = useCallback(async () => {
    if (!bodySystemToEdit) return;
    try {
      setIsLoading(true);
      setErrorMessage(null);
      await deleteBodySystem(bodySystemToEdit.id);

      dispatch(
        setBodySystemsList(
          bodySystemsList.filter(
            (bodysystem) => bodysystem.id !== bodySystemToEdit.id
          )
        )
      );
      dispatch(setBodySystemToEdit(null));
      dispatch(changeDeleteBodySystemModalVisibility(false));
      toast.success('Body system deleted successfully');
    } catch (err: any) {
      setErrorMessage(err.message);
      toast.error(err.message);
      dispatch(changeDeleteBodySystemModalVisibility(false));
    } finally {
      setIsLoading(false);
    }
  }, [bodySystemToEdit, bodySystemsList, dispatch]);

  return (
    <div className='scroll-container'>
      <div className='table-container body-systems-table'>
        <TableHeader headerColumns={headerColumns} gap='64px' />
        <div className='table-body' ref={scrollContainerRef}>
          {bodySystemsList?.map((bodySystem) => (
            <BodySystemsTableRow
              bodySystemData={bodySystem}
              key={bodySystem.id}
              onEditPrompt={onEditPrompt}
              onGenerateScores={onGenerateScores}
            />
          ))}
        </div>
      </div>
      {isDeleteBodySystemModalOpened && (
        <ConfirmModal
          isOpen={isDeleteBodySystemModalOpened}
          onClose={handleCloseDeleteModalClick}
          onConfirmClick={handleDelete}
          title='Delete this biomarker?'
          subtitle={
            errorMessage ||
            'This action will permanently remove the biomarker. Once deleted, it cannot be restored.'
          }
          loading={isLoading}
        />
      )}
    </div>
  );
};

export default BodySystemsTable;
